import React, { useState, useEffect, useRef } from 'react';
import { useGlobalSelectedId, onSetActiveIndicator, onSetActiveDBModuleId, onSetActiveRGModuleId, onSetActiveSTModuleId, onSetActiveModuleId, useGlobalActivePage } from '../../data/StatusStore';
import { d2gRound, d2gAvg, getNYCAVG } from '../utilities/Utilities';
import Colors from '../utilities/Colors';
import BarMeta from './BarMeta';
import { useData } from '../../data/DataContext';

export interface BarChartProps {
  options: any;
  data: any;
  width: number;
  height: number;
  colorClass: string;
}

const MARGIN_X = 0;
const MARGIN_Y = 15;

const Bar: React.FC<BarChartProps> = ({ options, data, width, height, colorClass }) => {
  const [preppedData, setPreppedData] = useState<any>([]);
  const [displayLen, setDisplayLen] = useState<any>({max:0, min:0});
  const [preppedLabelData, setPreppedLabelData] = useState<any>({});
  const [hoveredIndicator, setHoveredIndicator] = useState<any>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; obj: any } | null>(null); // Tooltip state
  const [barHeight, setBarHeight] = useState<number>(0);
  const [globalMaxValue, setGlobalMaxValue] = useState<number>(0);
  const selectedId = useGlobalSelectedId();
  const activePage = useGlobalActivePage();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width, height });
  const { dataNYC, dataCD } = useData();
  //console.log("B041025 options", options)
  useEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(entries => {
        if (!entries || entries.length === 0) return;
        const { width, height } = entries[0].contentRect;
        //console.log("121025 options.breadcrumb", options.breadcrumb);
        //console.log("121025 width, height", width, height);
        setDimensions({ width, height });
      });

      resizeObserver.observe(containerRef.current);

      return () => {
        if (containerRef.current) {
          resizeObserver.unobserve(containerRef.current);
        }
      };
    }
  }, []);

  useEffect(() => {
    if (selectedId) {
      prepData();
    }
  }, [selectedId]);

  const prepData = () => {
    if (selectedId) {
      const _filteredData = options.variables.filter((v: any) => !v.sort.Tooltip_only);
      const _tooltipData = options.variables.filter((v: any) => v.sort.Tooltip_only);
      const _globalMaxValue = Math.max(
        ...Object.values(data.dataJSON).flatMap((d: any) => {
          return _filteredData.map((v: any) => {
            const value = d[v.variable];
            return isNaN(value) ? 0 : value; // Filter out non-numeric values
          });
        })
      );
      setGlobalMaxValue(_globalMaxValue);
      const _preppedData = _filteredData.map((v: any) => {
        const rawValue = data.dataJSON[selectedId][v.variable];
        const isNA = rawValue === undefined || rawValue === null || rawValue === "NA";
        const value = !isNA && !isNaN(rawValue) ? rawValue : 'N/A';
        //const deviation = !isNA && !isNaN(rawValue) ? data.meta.avg[v.variable] - rawValue : 0;
        let ttVariable = '';
        _tooltipData.forEach((s: any) => {
          if (s.sort.Tooltip_only === v.variable) {
            ttVariable = s.variable;
          }
        });

        const tooltip = data.dataJSON[selectedId][ttVariable];
        const presentableTooltip = tooltip
          ? d2gRound(tooltip, { DESCRIPTOR: data.meta["DESCRIPTOR"][ttVariable] })
          : d2gRound(value, { DESCRIPTOR: data.meta["DESCRIPTOR"][v.variable] });

        const presentableLabel = tooltip
          ? data.meta["DISPLAY_NAME"][ttVariable].split("(")[0]
          : data.meta["DISPLAY_NAME"][v.variable].split("(")[0];
         console.log("050625 data", data);
        return {
          name: v.label,
          //deviation: isNA ? 0 : deviation,
          value,
          tooltip: presentableTooltip,
          tooltipLabel: presentableLabel,
          //avg: data.meta.avg[v.variable] ? data.meta.avg[v.variable] : null,
          avg: getNYCAVG(null, v.variable, data, dataNYC, dataCD), 
          //avg: dataNYC ? dataNYC.dataJSON[0][v.variable] : null, //data.meta.avg[v.variable],
          variable: v.variable,
          index: data.keyIndexDataArray[v.variable] ? data.keyIndexDataArray[v.variable][selectedId].index : 0,
          na: data.meta.na_count[v.variable],
          len: data.dataArray[v.variable] ? data.dataArray[v.variable].length : 0,
        };
      });

      const _preppedLabelData: { [key: string]: any } = {};
      _tooltipData.forEach((v: any) => {
        if (v.sort.Tooltip_only && v.sort.Tooltip_only !== "") {
          _preppedLabelData[v.sort.Tooltip_only] = {
            name: v.sort.DISPLAY_NAME,
            value: data.dataJSON[selectedId][v.variable],
            variable: v.variable,
          };
        }
      });

      setPreppedData(_preppedData);
      setPreppedLabelData(_preppedLabelData);
    }
  };

  useEffect(() => {
    setBarHeight(27.5); // Set a fixed height for each bar
    let longestDisplay = 0;
    let shortestDisplay = Infinity;
    preppedData.map((d: any, i: number) => {
        if (longestDisplay < d2gRound(d.value, { DESCRIPTOR: data.meta["DESCRIPTOR"][d.variable] }).toLocaleString().length) {
            longestDisplay = d2gRound(d.value, { DESCRIPTOR: data.meta["DESCRIPTOR"][d.variable] }).toLocaleString().length;
        }
        if (shortestDisplay > d2gRound(d.value, { DESCRIPTOR: data.meta["DESCRIPTOR"][d.variable] }).toLocaleString().length) {
            shortestDisplay = d2gRound(d.value, { DESCRIPTOR: data.meta["DESCRIPTOR"][d.variable] }).toLocaleString().length;
        }
    });
    setDisplayLen({max: longestDisplay, min: shortestDisplay});
  }, [preppedData]);

  const maxValue = Math.max(...preppedData.map((d: any) => d.value));

  const barScale = (value: number) => {
    if (!value) return 0;
    if (isNaN(value)) return 0;
    //console.log("B112824 preppedData", preppedData);
    //console.log("B112824 maxValue", maxValue);
    //console.log("B112824 globalMaxValue", globalMaxValue);
    const maxWidth = dimensions.width - (2 * MARGIN_X);
    //return ((value / maxValue) * maxWidth) / 2.5;
    //console.log("A011025 ((value / globalMaxValue) * maxWidth) / 2.5;", ((value / globalMaxValue) * maxWidth) / 2.5);
    //console.log("A011025 maxWidth", maxWidth);
    //console.log("A011025 globalMaxValue", globalMaxValue);
    //console.log("A011025 value", value);
    return ((value / globalMaxValue) * maxWidth) / 2.5;
  };
  const barAvg = (value: number) => {
    if (!value) return 0;
    if (isNaN(value)) return 0;
    //console.log("B112824 preppedData", preppedData);
    //console.log("B112824 maxValue", maxValue);
    //console.log("B112824 globalMaxValue", globalMaxValue);
    const maxWidth = dimensions.width - (2 * MARGIN_X);
    //return ((value / maxValue) * maxWidth) / 2.5;
    //console.log("A011025 value / globalMaxValue)", value / globalMaxValue);
    //console.log("A011025 maxWidth", maxWidth);
    //console.log("A011025 globalMaxValue", globalMaxValue);
    //console.log("A011025 value", value);
    return (value / globalMaxValue);
  };

  const onClicked = (variable: string) => {
    const hoveredVar = options.variables.find((v: any) => v.variable === variable);
    setHoveredIndicator(hoveredVar);
    //console.log("B112724 options.variables[0].sort.moduleId", options.variables[0].sort.moduleId);
    if (activePage !== "dashboard") {
      if (activePage === "stories") {
        onSetActiveSTModuleId(options.variables[0].sort.moduleId);
      }else if (activePage === "rg") {
        onSetActiveRGModuleId(options.variables[0].sort.moduleId);
      }else{
        onSetActiveModuleId(options.variables[0].sort.moduleId);
      }
    }else{
      onSetActiveDBModuleId(options.variables[0].sort.moduleId);
    }
    onSetActiveModuleId(options.variables[0].sort.moduleId);
    onSetActiveIndicator(variable, options.type.toLowerCase(), options.variables[0].sort.moduleId);
  };

  const handleMouseEnter = (event: React.MouseEvent<SVGRectElement>, obj: any) => {
    if (containerRef.current) {
      const { nativeEvent } = event;
      const offsetX = nativeEvent.offsetX;
      const offsetY = nativeEvent.offsetY;

      setTooltip({
        x: offsetX,
        y: offsetY,
        obj,
      });
    }
  };

  const handleMouseMove = (event: React.MouseEvent<SVGRectElement>) => {
    if (containerRef.current) {
      const { nativeEvent } = event;
      const offsetX = nativeEvent.offsetX;
      const offsetY = nativeEvent.offsetY;

      setTooltip((prev) => prev && {
        ...prev,
        x: offsetX,
        y: offsetY,
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  const fontSize = 12;
  const fontSize2 = 12;
  const labelWidth = 250;
  const chartWidth = `calc(100% - ${(labelWidth * 2 ) - 80}px)`;

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'flex-start', paddingRight: "16px" }}>
      <div style={{
          marginLeft: '0px',
          marginRight: '0px',
          fontSize: fontSize2,
          lineHeight: `${fontSize2 + 2}px`,
          width: "250px"
        }}>
        <BarMeta
          dataArray={data.dataArray[hoveredIndicator ? hoveredIndicator : options.variables[0]]}
          data={data}
          indicator={hoveredIndicator ? hoveredIndicator : options.variables[0]}
        />
      </div>
      <div style={{
          marginLeft: '0px',
          marginRight: '0px',
          marginTop: '13px',
          fontSize: fontSize2,
          lineHeight: `${fontSize2 + 2}px`,
          width: `${labelWidth}px`,
        }}
      >
        {preppedData.map((d: any, i: number) => (
          <div key={`info-${i}`}
            style={{
              fontSize: fontSize2,
              lineHeight: 0.95,
              display: "grid",
              justifyContent: "space-between",
              height: `${barHeight}px`,
              marginRight: "15px",
              cursor: 'pointer',
            }}
            onClick={() => onClicked(d.variable)}
          >
            <div style={{ width: "100%", alignContent: "center", paddingRight: "0px", fontWeight: "bold", fontSize: '13px' }}>
              {d.name.split("(")[0].replace("Industry Category: ", "").replace("Occupational Category: ", "")}
            </div>
          </div>
        ))}
      </div>
      <div style={{
          fontSize,
          lineHeight: `${fontSize + 2}px`,
          display: 'flex',
          width: '80px',
          position: "relative"
        }}
      >
        <svg width="100%" height={barHeight * preppedData.length + 15} style={{ display: "inline-block" }}>
          <g transform={`translate(${MARGIN_X}, 0)`}>
            {preppedData.map((d: any, i: number) => (
              <g key={`text-bar-${i}`} transform={`translate(0, ${i * barHeight + 15})`}>
                <text
                  x={5}
                  y={barHeight / 2}
                  alignmentBaseline="middle"
                  textAnchor="start"
                  fill={Colors.processBlack}
                  fontSize={fontSize}
                  pointerEvents="none"
                  letterSpacing= {displayLen.max > 10 ? '-0.5px' : 'normal'}
                >
                  { `${d2gRound(d.value, { DESCRIPTOR: data.meta["DESCRIPTOR"][d.variable] })}`}
                </text>
              </g>
            ))}
          </g>
        </svg>
      </div>
      <div style={{
          fontSize,
          lineHeight: `${fontSize + 2}px`,
          display: 'flex',
          width: chartWidth,
          position: "relative",
        }}
      >
        <svg key={`bar-container-${options.variables[0].sort.Sort["Unique Module ID"]}`} width="100%" height={barHeight * preppedData.length + 15} style={{ display: "inline-block" }}>
          <g transform={`translate(${MARGIN_X}, 0)`}>
            {preppedData.map((d: any, i: number) => {
              /*if (i === 0 && options.breadcrumb === "Education > Resources > Afterschool Programs-Education > Ungrouped > bar"){
                console.log("A121025 options", options)
                console.log("A121025 d.value", d.value)
                console.log("A121025 barScale(d.value)", barScale(d.value))
                console.log("A121025 d.avg", d.avg)
                console.log("A121025 barScale(d.avg)", barScale(d.avg))
                console.log("A121025 preppedData", preppedData)
              }*/
              return (
              <g key={`parent-bar-${i}-${d.variable}`}>
                {i === 0 
                  ? <text 
                      key={`text-bar-${i}-${d.variable}`} 
                      x={barAvg(d.value) < 0.5
                        ? barScale(d.avg) + 0
                        : barScale(d.avg) - 0
                      }
                      y={9.5}
                      alignmentBaseline="middle"
                      textAnchor={barScale(d.avg) < 0.5
                        ? "start"
                        : "end"
                      }
                      fill="#000"
                      fontSize={fontSize}
                    >
                      { d2gAvg({ DESCRIPTOR: data.meta["DESCRIPTOR"][d.variable] })}
                    </text>
                 : null}
                <g key={`bar-${i}-${d.variable}`} transform={`translate(0, ${i * barHeight + 15})`}>
                <rect
                  x={0}
                  y={0}
                  width={barScale(d.value)}
                  height={barHeight - 2}
                  fill={Colors.getColorByClass(`${colorClass}_variant`)}
                  //fill={Colors.getColorQuintile(d.index, d.na, d.len, `${colorClass}_variant`)}
                  onClick={() => onClicked(d.variable)}
                  onMouseEnter={(e) => handleMouseEnter(e, d)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  style={{ cursor: 'pointer', transition: 'width 0.5s ease, fill 0.5s ease' }}
                />
                <line
                  key={`bar-${i}-${d.variable}-line`}
                  x1={barScale(d.avg)}
                  y1={0}
                  x2={barScale(d.avg)}
                  y2={barHeight - 2}
                  stroke="#9E9C9C"
                  strokeWidth={1}
                  strokeDasharray="4"
                />
                </g></g>
            )})}
          </g>
        </svg>
        {tooltip && (
          <div
            style={{
              position: 'absolute',
              top: tooltip.y - 60,
              left: tooltip.x - 75,
              backgroundColor: '#fff',
              border: '2px solid #ccc',
              borderRadius: '6px',
              padding: '8px 12px',
              pointerEvents: 'none',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
              zIndex: 10,
              color: '#333',
              width:'150px',
              height:'50px',
              textAlign: 'left',
              wordBreak: 'break-word', // Ensures long words or strings wrap correctly              
              whiteSpace: 'normal', // Allows text to wrap
              maxWidth: '150px', // Set a max width for the tooltip
              lineHeight: '0.9',
            }}
          >
            <b>{tooltip.obj.tooltip}</b> <span style={{ fontSize: '10px', color: '#666' }}>{tooltip.obj.tooltipLabel}</span>
            <div
              style={{
                position: 'absolute',
                bottom: '-7px',
                left: '45%',
                width: '12px',
                height: '12px',
                backgroundColor: '#fff',
                borderLeft: '2px solid #ccc',
                borderBottom: '2px solid #ccc',
                transform: 'rotate(-45deg)',
                boxShadow: '-1px 1px 2px rgba(0, 0, 0, 0.1)',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Bar;
